<template>
  <div class="app-details-container">
    <div class="app-description">{{ currentApp.description }}</div>

    <div class="route-tabs">
      <b-nav tabs>
        <b-nav-item
          v-if="!isCustomContent"
          to="data-palette"
          exact
          exact-active-class="active"
        >
          {{ dataPaletteText }}
        </b-nav-item>

        <b-nav-item v-if="!isCustomContent" to="pages" active-class="active">
          {{ pagesText }}
        </b-nav-item>

        <b-nav-item
          v-if="shouldShowDeploy"
          :to="{ name: 'Deploy', params: { appId: appId } }"
          exact
          exact-active-class="active"
        >
          {{ deployText }}
        </b-nav-item>

        <b-nav-item to="settings" exact exact-active-class="active">
          {{ settingsText }}
        </b-nav-item>
      </b-nav>

      <div class="deployable-wrapper">
        <div class="icon-label">{{ deployableText }}</div>
        <svg
          class="check-icon deployable-icon"
          v-if="!isFetchingManifests && isContentDeployable"
        >
          <use
            xlink:href="@/assets/icons/utility-sprite/svg/symbols.svg#check"
          ></use>
        </svg>

        <svg
          class="error-icon deployable-icon"
          v-if="!isFetchingManifests && !isContentDeployable"
        >
          <use
            xlink:href="@/assets/icons/utility-sprite/svg/symbols.svg#error"
          ></use>
        </svg>

        <div class="loader loader-small" v-if="isFetchingManifests"></div>
      </div>

      <div class="btn-wrapper">
        <div class="loader" v-if="loadingData"></div>

        <div
          class="create-btn-wrapper"
          v-if="!loadingData && currentApp.isTemplate"
        >
          <b-dropdown
            split
            size="sm"
            toggle-class="custom-dropdown"
            variant="none"
            class="btn-grp"
            menu-class="w-100"
          >
            <template #button-content>
              <button
                class="nt-btn"
                :class="{ 'disabled-btn': !isContentDeployable }"
                :disabled="!isContentDeployable"
                @click="saveAndUpdate"
              >
                {{ $t("saveAndUpdatePageTemplate") }}
              </button>
            </template>

            <b-dropdown-item-button @click="regularSave">
              {{ saveText }}
            </b-dropdown-item-button>
          </b-dropdown>
        </div>

        <button
          v-if="!loadingData && !currentApp.isTemplate && !isCustomContent"
          class="slds-btn save-btn no-user-select"
          @click="regularSave"
        >
          {{ saveText }}
        </button>

        <button
          v-if="!loadingData && !currentApp.isTemplate && isCustomContent"
          class="slds-btn save-btn no-user-select"
          v-b-modal="'new-ccv-' + this.appId"
        >
          {{ newVersion }}
        </button>

        <new-custom-content-version-modal
          v-if="isCustomContent"
          :app="currentApp"
        ></new-custom-content-version-modal>
      </div>
    </div>

    <router-view></router-view>
  </div>
</template>

<script>
  import { mapState } from "vuex";
  import utils from "@/utils";
  import services from "@/services";
  import store from "@/store";
  import {
    MUTATION_NAME,
    HTML_REPORT_FIELD_NAMES,
    TOAST_CONFIG
  } from "@/static/global-variables";
  import { QUERIES } from "@/static/queries";
  import NewCustomContentVersionModal from "@/components/NewCustomContentVersionModal.vue";

  export default {
    name: "AppDetails",
    components: { NewCustomContentVersionModal },
    data() {
      return {
        loadingData: false
      };
    },
    mounted() {
      if (this.contextDataFailed) {
        this.$bvToast.toast(
          this.messages.SFDC_DATA_ERROR.Text_vod__c,
          TOAST_CONFIG.BOTTOM_CENTER_ERROR
        );
      }

      if (this.dataInfo.errorLoadingLocal) {
        this.$bvToast.toast(
          this.messages.ERROR_WHILE_LOADING.Text_vod__c,
          TOAST_CONFIG.BOTTOM_CENTER_ERROR
        );
      }

      if (this.dataInfo.isDirty) {
        // this.dataInfo will be null if reloading under page tab
        // keep a local copy to avoid null error
        const local = this.dataInfo.local;
        const server = this.dataInfo.server;
        this.$bvModal
          .msgBoxConfirm(this.messages.RESTORE_UNSAVED_PROMPT.Text_vod__c, {
            size: "sm",
            buttonSize: "sm",
            cancelVariant: "danger",
            okVariant: "secondary",
            okTitle: this.messages.RESTORE.Text_vod__c,
            cancelTitle: this.$t("delete"),
            footerClass: "p-2",
            hideHeaderClose: false,
            centered: true
          })
          .then(value => {
            if (value) {
              server.isDirty = true;
              store.commit(MUTATION_NAME.UPDATE_APP, local);
            } else {
              this.$bvModal
                .msgBoxConfirm(this.messages.CONFIRM_DELETE.Text_vod__c, {
                  size: "sm",
                  buttonSize: "sm",
                  okVariant: "danger",
                  okTitle: this.messages.OK.Text_vod__c,
                  cancelTitle: this.messages.CANCEL.Text_vod__c,
                  footerClass: "p-2",
                  hideHeaderClose: false,
                  centered: true
                })
                .then(value => {
                  if (value) {
                    server.isDirty = false;
                    server.name = this.currentApp.name;
                    server.type = this.currentApp.type;
                    server.dataElements = server.dataElements || {};
                    store.commit(MUTATION_NAME.UPDATE_APP, server);
                  } else {
                    server.isDirty = true;
                    store.commit(MUTATION_NAME.UPDATE_APP, local);
                  }
                })
                .catch(console.error);
            }
          })
          .catch(console.error);
      }
    },
    computed: {
      ...mapState({
        apps: "apps",
        user: "user",
        objectDescribe: "objectDescribe",
        messages: "messages",
        currentContent: "currentContent",
        isFetchingManifests: "isFetchingManifests"
      }),
      currentApp() {
        return utils.findAppByAppId(this.apps, this.appId);
      },
      dataPaletteText() {
        return this.getMessageText(this.messages.DATA_PALETTE);
      },
      pagesText() {
        return this.$t("layout");
      },
      deployText() {
        return this.getMessageText(this.messages.DEPLOY);
      },
      settingsText() {
        return this.getMessageText(this.messages.SETTINGS);
      },
      saveText() {
        return this.getMessageText(this.messages.SAVE);
      },
      newVersion() {
        return this.$t("newVersion");
      },
      deployableText() {
        return this.getMessageText(this.messages.DEPLOYABLE);
      },
      shouldShowDeploy() {
        let fieldFLS = utils.findFieldFLS(
          this.objectDescribe[QUERIES.htmlReport.object],
          HTML_REPORT_FIELD_NAMES.Studio_Id_vod__c
        );
        return fieldFLS.hasRead;
      },
      isContentDeployable() {
        return this.currentContent && this.currentContent.isDeployable;
      },
      isCustomContent() {
        return this.currentApp.customContent;
      }
    },
    props: {
      appId: String,
      dataInfo: Object,
      contextDataFailed: Boolean
    },
    methods: {
      performSave(updateVersion) {
        this.loadingData = true;
        delete this.currentApp.isDirty;
        const component = this;
        services
          .saveApp(this.currentApp, updateVersion, this.isContentDeployable)
          .then(
            resp => {
              component.currentApp.isDirty = false;
              let app = JSON.parse(JSON.stringify(component.currentApp));

              if (app.isTemplate && updateVersion) {
                app.version = resp.data.data.version;
              }
              store.commit(MUTATION_NAME.UPDATE_APP, app);
              component.loadingData = false;
              component.$bvToast.toast(
                component.getMessageText(component.messages.SAVE_SUCCESS),
                TOAST_CONFIG.BOTTOM_CENTER_SUCCESS
              );
            },
            () => {
              component.loadingData = false;
              component.$bvToast.toast(
                component.getMessageText(component.messages.SAVE_ERROR),
                TOAST_CONFIG.BOTTOM_CENTER_ERROR
              );
            }
          );
      },
      saveAndUpdate() {
        this.performSave(true);
      },

      regularSave() {
        this.performSave(false);
      }
    }
  };
</script>

<style scoped>
  .route-tabs {
    display: flex;
  }

  .route-tabs .nav-tabs {
    flex-basis: 100%;
  }

  .slds-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    padding: 0;
    background: transparent;
    background-clip: border-box;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    line-height: 1.875rem;
    color: #0070d2;
    -webkit-appearance: none;
    white-space: nowrap;
    text-decoration: none;
  }

  .save-btn {
    margin-left: 0.5rem;
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: center;
    vertical-align: middle;
    justify-content: center;
    transition: border 0.15s linear;
    background-color: #0070d2;
    border: 1px solid #0070d2;
    color: #fff;
  }

  .btn-wrapper {
    border-bottom: 1px solid #dee2e6;
    padding-right: 2rem;
  }

  .deployable-wrapper {
    border-bottom: 1px solid #dee2e6;
    padding-right: 3rem;
    display: flex;
    font-size: 0.8125rem;
    align-items: center;
  }

  .icon-label {
    margin-right: 0.5rem;
  }

  .deployable-icon {
    height: 1rem;
    width: 1rem;
  }

  .check-icon {
    fill: var(--GREEN);
  }

  .error-icon {
    fill: var(--SF_ERROR_RED);
  }

  .app-description {
    padding: 0.5rem 1rem;
    overflow-wrap: break-word;
  }

  .loader-small {
    width: 24px;
    height: 24px;
  }
</style>
